// Date picker component with dropdown calendar interface (Google Calendar style)
// CREATED: Provides date selection via inline calendar with month navigation
// Features: Smart positioning, swipe support, mobile-responsive, auto-scroll to selected month
// Replaces native date inputs with consistent cross-browser/cross-device UX
// Matches visual pattern of TimePicker and DurationPicker for harmonious UI
// UPDATED: Added compact prop for short date format (no weekday) — used in filter bars

import { useState, useRef, useEffect } from 'react';
import { Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
  value: string | null;
  onChange: (date: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
  minDate?: string;
  maxDate?: string;
  compact?: boolean;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Sélectionnez une date',
  disabled = false,
  className = '',
  label,
  required = false,
  minDate,
  maxDate,
  compact = false,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(value ? new Date(value) : new Date());
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    vertical: 'bottom' | 'top';
    horizontal: 'left' | 'right';
  }>({ vertical: 'bottom', horizontal: 'left' });

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse value to show current month when opening
  useEffect(() => {
    if (value) {
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  // Calculate optimal dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = 380; // Approximate height
      const dropdownWidth = 320; // w-80 = 320px

      // Check vertical space
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const vertical = spaceBelow >= dropdownHeight || spaceBelow > spaceAbove ? 'bottom' : 'top';

      // Check horizontal space
      const spaceRight = window.innerWidth - rect.left;
      const horizontal = spaceRight >= dropdownWidth ? 'left' : 'right';

      setDropdownPosition({ vertical, horizontal });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
  };

  const formatDateForDisplay = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (compact) {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
    const formatted = date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
  ];

  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  // Swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNextMonth();
    }
    if (isRightSwipe) {
      goToPreviousMonth();
    }
  };

  const handleDayClick = (day: number, monthOffset: number = 0) => {
    const targetMonth = monthOffset !== 0
      ? new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset)
      : currentMonth;

    const year = targetMonth.getFullYear();
    const month = String(targetMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateStr = `${year}-${month}-${dayStr}`;

    // Check if date is within min/max range
    if (minDate && dateStr < minDate) return;
    if (maxDate && dateStr > maxDate) return;

    onChange(dateStr);
    setIsOpen(false);
  };

  const isToday = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (day: number) => {
    if (!value) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const selected = new Date(value);
    return date.toDateString() === selected.toDateString();
  };

  const isDisabled = (day: number, monthOffset: number = 0): boolean => {
    const targetMonth = monthOffset !== 0
      ? new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset)
      : currentMonth;

    const year = targetMonth.getFullYear();
    const month = String(targetMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateStr = `${year}-${month}-${dayStr}`;

    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    ).getDate();

    // Get first day of month (0 = Sunday, 1 = Monday, etc.)
    // Adjust to make Monday = 0, Sunday = 6
    const firstDayOfMonthRaw = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    ).getDay();
    const firstDayOfMonth = firstDayOfMonthRaw === 0 ? 6 : firstDayOfMonthRaw - 1;

    // Get last month's info for adjacent days
    const prevMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    const daysInPrevMonth = new Date(
      prevMonthDate.getFullYear(),
      prevMonthDate.getMonth() + 1,
      0
    ).getDate();

    const days = [];

    // Add previous month's trailing days
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const disabled = isDisabled(day, -1);
      days.push(
        <button
          key={`prev-${day}`}
          type="button"
          onClick={() => !disabled && handleDayClick(day, -1)}
          disabled={disabled}
          className={`min-h-[40px] min-w-[40px] flex items-center justify-center transition-all rounded-lg text-sm font-medium ${
            disabled
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          }`}
        >
          {day}
        </button>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const todayClass = isToday(day);
      const selectedClass = isSelected(day);
      const disabled = isDisabled(day);

      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dateString = date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => !disabled && handleDayClick(day)}
          disabled={disabled}
          className={`min-h-[40px] min-w-[40px] flex items-center justify-center transition-all rounded-lg text-sm font-medium ${
            selectedClass
              ? 'bg-primary-500 text-white font-semibold'
              : todayClass
              ? 'bg-primary-50 text-primary-700 border border-primary-300'
              : disabled
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
          aria-label={selectedClass ? `${dateString} (sélectionné)` : todayClass ? `${dateString} (aujourd'hui)` : dateString}
          aria-selected={selectedClass}
          aria-disabled={disabled}
        >
          {day}
        </button>
      );
    }

    // Calculate the number of weeks needed (5 or 6)
    const totalDaysNeeded = firstDayOfMonth + daysInMonth;
    const weeksNeeded = totalDaysNeeded <= 35 ? 5 : 6;
    const totalCellsNeeded = weeksNeeded * 7;

    // Add next month's leading days to fill the grid
    const totalCells = days.length;
    const remainingCells = totalCellsNeeded - totalCells;
    for (let day = 1; day <= remainingCells && day <= 14; day++) {
      const disabled = isDisabled(day, 1);
      days.push(
        <button
          key={`next-${day}`}
          type="button"
          onClick={() => !disabled && handleDayClick(day, 1)}
          disabled={disabled}
          className={`min-h-[40px] min-w-[40px] flex items-center justify-center transition-all rounded-lg text-sm font-medium ${
            disabled
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const displayValue = value ? formatDateForDisplay(value) : placeholder;
  const hasValue = !!value;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center gap-3 px-4 py-2 border rounded-lg transition-all text-sm ${
          disabled
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            : hasValue
            ? 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
        }`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={label || 'Sélectionner une date'}
      >
        <Calendar size={18} className={hasValue ? 'text-gray-600' : 'text-gray-400'} />
        <span className="flex-1 text-left">{displayValue}</span>
        {hasValue && !disabled && (
          <X
            size={18}
            className="text-gray-400 hover:text-gray-600"
            onClick={handleClear}
          />
        )}
      </button>

      {isOpen && (
        <div
          className={`fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[60] ${
            dropdownPosition.vertical === 'bottom' ? 'mt-2' : 'mb-2'
          }`}
          style={{
            [dropdownPosition.vertical === 'bottom' ? 'top' : 'bottom']:
              dropdownPosition.vertical === 'bottom'
                ? `${(dropdownRef.current?.getBoundingClientRect().bottom || 0) + 8}px`
                : `${window.innerHeight - (dropdownRef.current?.getBoundingClientRect().top || 0) + 8}px`,
            [dropdownPosition.horizontal === 'left' ? 'left' : 'right']:
              dropdownPosition.horizontal === 'left'
                ? `${dropdownRef.current?.getBoundingClientRect().left || 0}px`
                : `${window.innerWidth - (dropdownRef.current?.getBoundingClientRect().right || 0)}px`,
            width: '320px',
          }}
          role="dialog"
          aria-label="Calendrier"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-700">Sélectionner la date</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fermer"
              type="button"
            >
              <X size={18} />
            </button>
          </div>

          {/* Calendar */}
          <div className="px-4 py-4">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={goToPreviousMonth}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                aria-label="Mois précédent"
              >
                <ChevronLeft size={20} />
              </button>
              <h3 className="text-sm font-semibold text-gray-900">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <button
                type="button"
                onClick={goToNextMonth}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                aria-label="Mois suivant"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Day names */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((name) => (
                <div
                  key={name}
                  className="text-center text-xs font-semibold text-gray-500"
                >
                  {name}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
